/* ========================================================================
   MANOMAZE — WebGL Hero Slider (vanilla JS port)
   Requires: Three.js r128 + GSAP 3.12+ loaded globally before this script
   Slides data expected at window.MANOMAZE_SLIDES
   ======================================================================== */

(function () {
  'use strict';

  function loadScript(src, globalName) {
    return new Promise(function (resolve, reject) {
      if (window[globalName]) { resolve(); return; }
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        var check = setInterval(function () {
          if (window[globalName]) { clearInterval(check); resolve(); }
        }, 50);
        setTimeout(function () { clearInterval(check); reject(new Error('Timeout ' + globalName)); }, 10000);
        return;
      }
      var s = document.createElement('script');
      s.src = src;
      s.onload = function () { setTimeout(resolve, 100); };
      s.onerror = function () { reject(new Error('Failed ' + src)); };
      document.head.appendChild(s);
    });
  }

  function bootstrap() {
    Promise.resolve()
      .then(function () { return loadScript('https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js', 'gsap'); })
      .then(function () { return loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js', 'THREE'); })
      .then(initApplication)
      .catch(function (e) { console.error('[MANOMAZE] Failed to load libs', e); });
  }

  function initApplication() {
    var SLIDER_CONFIG = {
      settings: {
        transitionDuration: 2.2,
        autoSlideSpeed: 5500,
        currentEffect: 'glass',
        globalIntensity: 1.0,
        speedMultiplier: 1.0,
        distortionStrength: 1.0,
        colorEnhancement: 1.0,
        glassRefractionStrength: 1.0,
        glassChromaticAberration: 1.0,
        glassBubbleClarity: 1.0,
        glassEdgeGlow: 1.0,
        glassLiquidFlow: 1.0
      }
    };

    var slides = (window.MANOMAZE_SLIDES && window.MANOMAZE_SLIDES.length) ? window.MANOMAZE_SLIDES : [];
    if (!slides.length) { console.warn('[MANOMAZE] No slides provided'); return; }

    var currentSlideIndex = 0;
    var isTransitioning = false;
    var shaderMaterial, renderer, scene, camera;
    var slideTextures = [];
    var texturesLoaded = false;
    var autoSlideTimer = null;
    var progressAnimation = null;
    var sliderEnabled = false;

    var SLIDE_DURATION = function () { return SLIDER_CONFIG.settings.autoSlideSpeed; };
    var PROGRESS_UPDATE_INTERVAL = 50;
    var TRANSITION_DURATION = function () { return SLIDER_CONFIG.settings.transitionDuration; };

    function computeImageScale() {
      // CONTAIN mode — base scale, square images get further -20% in shader
      var aspect = window.innerWidth / window.innerHeight;
      if (aspect > 1.5) return 0.88;     // wide desktop
      if (aspect > 1.0) return 0.92;     // landscape phone
      if (aspect > 0.7) return 0.95;     // tablet portrait
      return 0.98;                        // narrow phone — almost full width (rectangulars OK)
    }
    function computeImageOffsetY() {
      // 0 = top, 0.5 = center, 1 = bottom. Lower value = image HIGHER on screen
      var aspect = window.innerWidth / window.innerHeight;
      if (aspect > 1.5) return 0.08;     // wide desktop: near top
      if (aspect > 1.0) return 0.08;
      if (aspect > 0.7) return 0.03;
      return 0.02;                        // narrow phone: very top
    }
    function computeSquareOffsetY() {
      // 0 = top, 1 = bottom (CSS convention; flipped in shader)
      // Squares: leave a bit of room below the header logo / label
      var aspect = window.innerWidth / window.innerHeight;
      if (aspect > 1.5) return 0.22;     // wide desktop: a bit below top label
      if (aspect > 1.0) return 0.18;     // landscape phone
      if (aspect > 0.7) return 0.10;     // tablet portrait
      return 0.10;                        // narrow phone: small gap from header
    }
    function computeRectScaleMult() {
      // Rectangle (non-square) images: aggressive enlargement (+20% from prev)
      var aspect = window.innerWidth / window.innerHeight;
      if (aspect > 1.5) return 2.34;     // wide desktop
      if (aspect > 1.0) return 2.10;     // landscape phone
      if (aspect > 0.7) return 1.86;     // tablet portrait
      return 1.80;                        // narrow phone
    }
    function computeRectOffsetY() {
      // Rectangles: small space from top on mobile, normal on desktop
      var aspect = window.innerWidth / window.innerHeight;
      if (aspect > 1.5) return 0.10;     // wide desktop
      if (aspect > 1.0) return 0.08;     // landscape phone
      if (aspect > 0.7) return 0.05;     // tablet portrait
      return 0.05;                        // narrow phone
    }

    var vertexShader =
      'varying vec2 vUv;' +
      'void main() {' +
      '  vUv = uv;' +
      '  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);' +
      '}';

    var fragmentShader = [
      'uniform sampler2D uTexture1, uTexture2;',
      'uniform float uProgress;',
      'uniform vec2 uResolution, uTexture1Size, uTexture2Size;',
      'uniform float uGlobalIntensity, uSpeedMultiplier, uDistortionStrength;',
      'uniform float uGlassRefractionStrength, uGlassChromaticAberration, uGlassBubbleClarity, uGlassEdgeGlow, uGlassLiquidFlow;',
      'uniform float uImageScale;',
      'uniform float uImageOffsetY;',
      'uniform float uSquareOffsetY;',
      'uniform float uRectScaleMult;',
      'uniform float uRectOffsetY;',
      'varying vec2 vUv;',
      'vec2 getCoverUV(vec2 uv, vec2 textureSize) {',
      '  vec2 s = uResolution / textureSize;',
      '  // CONTAIN mode with per-aspect, viewport-aware tuning:',
      '  //   squares (1:1) → keep -20% scale, position via uSquareOffsetY',
      '  //   rectangles → scale via uRectScaleMult, position via uRectOffsetY',
      '  float texAspect = textureSize.x / textureSize.y;',
      '  float squareness = 1.0 - smoothstep(0.0, 0.25, abs(texAspect - 1.0));',
      '  float scaleMult = mix(uRectScaleMult, 1.04, squareness);',
      '  float effectiveOffsetY = mix(uRectOffsetY, uSquareOffsetY, squareness);',
      '  float scale = min(s.x, s.y) * uImageScale * scaleMult;',
      '  vec2 scaledSize = textureSize * scale;',
      '  vec2 offset;',
      '  offset.x = (uResolution.x - scaledSize.x) * 0.5;',
      '  // FIX: WebGL Y is bottom-up; flip so 0=top and 1=bottom (CSS-style)',
      '  offset.y = (uResolution.y - scaledSize.y) * (1.0 - effectiveOffsetY);',
      '  return (uv * uResolution - offset) / scaledSize;',
      '}',
      'float inBounds(vec2 uv) {',
      '  vec2 b = step(0.0, uv) * step(uv, vec2(1.0));',
      '  return b.x * b.y;',
      '}',
      'void main() {',
      '  float progress = uProgress;',
      '  float time = progress * 5.0 * uSpeedMultiplier;',
      '  vec2 uv1 = getCoverUV(vUv, uTexture1Size);',
      '  vec2 uv2 = getCoverUV(vUv, uTexture2Size);',
      '  float maxR = length(uResolution) * 0.85;',
      '  float br = progress * maxR;',
      '  vec2 p = vUv * uResolution;',
      '  vec2 c = uResolution * 0.5;',
      '  float d = length(p - c);',
      '  float nd = d / max(br, 0.001);',
      '  float param = smoothstep(br + 3.0, br - 3.0, d);',
      '  vec4 img;',
      '  if (param > 0.0) {',
      '    float ro = 0.08 * uGlassRefractionStrength * uDistortionStrength * uGlobalIntensity * pow(smoothstep(0.3 * uGlassBubbleClarity, 1.0, nd), 1.5);',
      '    vec2 dir = (d > 0.0) ? (p - c) / d : vec2(0.0);',
      '    vec2 distUV = uv2 - dir * ro;',
      '    distUV += vec2(sin(time + nd * 10.0), cos(time * 0.8 + nd * 8.0)) * 0.015 * uGlassLiquidFlow * uSpeedMultiplier * nd * param;',
      '    float ca = 0.02 * uGlassChromaticAberration * uGlobalIntensity * pow(smoothstep(0.3, 1.0, nd), 1.2);',
      '    img = vec4(',
      '      texture2D(uTexture2, distUV + dir * ca * 1.2).r,',
      '      texture2D(uTexture2, distUV + dir * ca * 0.2).g,',
      '      texture2D(uTexture2, distUV - dir * ca * 0.8).b,',
      '      1.0);',
      '    if (uGlassEdgeGlow > 0.0) {',
      '      float rim = smoothstep(0.95, 1.0, nd) * (1.0 - smoothstep(1.0, 1.01, nd));',
      '      img.rgb += rim * 0.10 * uGlassEdgeGlow * uGlobalIntensity;',
      '    }',
      '  } else { img = texture2D(uTexture2, uv2); }',
      '  vec4 oldImg = texture2D(uTexture1, uv1);',
      '  if (progress > 0.95) img = mix(img, texture2D(uTexture2, uv2), (progress - 0.95) / 0.05);',
      '  vec4 result = mix(oldImg, img, param);',
      '  // Mask out-of-bound regions with pure black (image fits inside viewport)',
      '  float mask = mix(inBounds(uv1), inBounds(uv2), param);',
      '  gl_FragColor = vec4(result.rgb * mask, 1.0);',
      '}'
    ].join('\n');

    function splitText(text) {
      return text.split('').map(function (ch) {
        return '<span style="display:inline-block;opacity:0;">' + (ch === ' ' ? '&nbsp;' : ch) + '</span>';
      }).join('');
    }

    function updateContent(idx) {
      var titleEl = document.getElementById('mainTitle');
      var descEl = document.getElementById('mainDesc');
      var labelEl = document.getElementById('mainLabel');
      if (!titleEl || !descEl) return;

      gsap.to(titleEl.children, { y: -20, opacity: 0, duration: 0.5, stagger: 0.02, ease: 'power2.in' });
      gsap.to(descEl, { y: -10, opacity: 0, duration: 0.4, ease: 'power2.in' });
      if (labelEl) gsap.to(labelEl, { y: -10, opacity: 0, duration: 0.4, ease: 'power2.in' });

      setTimeout(function () {
        titleEl.innerHTML = splitText(slides[idx].title);
        descEl.textContent = slides[idx].description;
        if (labelEl) {
          labelEl.textContent = slides[idx].material || '';
          labelEl.style.display = (slides[idx].material) ? 'inline-block' : 'none';
        }
        gsap.set(titleEl.children, { opacity: 0 });
        gsap.set(descEl, { y: 20, opacity: 0 });
        if (labelEl) gsap.set(labelEl, { y: 10, opacity: 0 });

        var children = titleEl.children;
        var animIdx = idx % 6;

        switch (animIdx) {
          case 0:
            gsap.set(children, { y: 20 });
            gsap.to(children, { y: 0, opacity: 1, duration: 0.8, stagger: 0.03, ease: 'power3.out' });
            break;
          case 1:
            gsap.set(children, { y: -20 });
            gsap.to(children, { y: 0, opacity: 1, duration: 0.8, stagger: 0.03, ease: 'back.out(1.7)' });
            break;
          case 2:
            gsap.set(children, { filter: 'blur(10px)', scale: 1.5, y: 0 });
            gsap.to(children, { filter: 'blur(0px)', scale: 1, opacity: 1, duration: 1, stagger: { amount: 0.5, from: 'random' }, ease: 'power2.out' });
            break;
          case 3:
            gsap.set(children, { scale: 0, y: 0 });
            gsap.to(children, { scale: 1, opacity: 1, duration: 0.6, stagger: 0.05, ease: 'back.out(1.5)' });
            break;
          case 4:
            gsap.set(children, { rotationX: 90, y: 0, transformOrigin: '50% 50%' });
            gsap.to(children, { rotationX: 0, opacity: 1, duration: 0.8, stagger: 0.04, ease: 'power2.out' });
            break;
          case 5:
            gsap.set(children, { x: 30, y: 0 });
            gsap.to(children, { x: 0, opacity: 1, duration: 0.8, stagger: 0.03, ease: 'power3.out' });
            break;
        }
        gsap.to(descEl, { y: 0, opacity: 1, duration: 0.8, delay: 0.2, ease: 'power3.out' });
        if (labelEl) gsap.to(labelEl, { y: 0, opacity: 1, duration: 0.7, delay: 0.05, ease: 'power3.out' });
      }, 500);
    }

    function navigateToSlide(targetIndex) {
      if (isTransitioning || targetIndex === currentSlideIndex) return;
      stopAutoSlideTimer();
      quickResetProgress(currentSlideIndex);

      var currentTexture = slideTextures[currentSlideIndex];
      var targetTexture = slideTextures[targetIndex];
      if (!currentTexture || !targetTexture) return;

      isTransitioning = true;
      shaderMaterial.uniforms.uTexture1.value = currentTexture;
      shaderMaterial.uniforms.uTexture2.value = targetTexture;
      shaderMaterial.uniforms.uTexture1Size.value = currentTexture.userData.size;
      shaderMaterial.uniforms.uTexture2Size.value = targetTexture.userData.size;

      updateContent(targetIndex);

      currentSlideIndex = targetIndex;
      updateCounter(currentSlideIndex);
      updateNavigationState(currentSlideIndex);

      gsap.fromTo(shaderMaterial.uniforms.uProgress,
        { value: 0 },
        {
          value: 1,
          duration: TRANSITION_DURATION(),
          ease: 'power2.inOut',
          onComplete: function () {
            shaderMaterial.uniforms.uProgress.value = 0;
            shaderMaterial.uniforms.uTexture1.value = targetTexture;
            shaderMaterial.uniforms.uTexture1Size.value = targetTexture.userData.size;
            isTransitioning = false;
            safeStartTimer(100);
          }
        }
      );
    }

    function handleSlideChange() {
      if (isTransitioning || !texturesLoaded || !sliderEnabled) return;
      navigateToSlide((currentSlideIndex + 1) % slides.length);
    }

    function createSlidesNavigation() {
      var nav = document.getElementById('slidesNav');
      if (!nav) return;
      nav.innerHTML = '';
      slides.forEach(function (slide, i) {
        var item = document.createElement('div');
        item.className = 'slide-nav-item' + (i === 0 ? ' active' : '');
        item.dataset.slideIndex = String(i);
        item.innerHTML =
          '<div class="slide-progress-line"><div class="slide-progress-fill"></div></div>' +
          '<div class="slide-nav-title">' + slide.title + '</div>';
        item.addEventListener('click', function (e) {
          e.stopPropagation();
          if (!isTransitioning && i !== currentSlideIndex) {
            stopAutoSlideTimer();
            quickResetProgress(currentSlideIndex);
            navigateToSlide(i);
          }
        });
        nav.appendChild(item);
      });
    }

    function updateNavigationState(idx) {
      document.querySelectorAll('.slide-nav-item').forEach(function (el, i) {
        el.classList.toggle('active', i === idx);
      });
    }
    function updateSlideProgress(idx, prog) {
      var items = document.querySelectorAll('.slide-nav-item');
      var el = items[idx] ? items[idx].querySelector('.slide-progress-fill') : null;
      if (el) { el.style.width = prog + '%'; el.style.opacity = '1'; }
    }
    function fadeSlideProgress(idx) {
      var items = document.querySelectorAll('.slide-nav-item');
      var el = items[idx] ? items[idx].querySelector('.slide-progress-fill') : null;
      if (el) { el.style.opacity = '0'; setTimeout(function () { el.style.width = '0%'; }, 300); }
    }
    function quickResetProgress(idx) {
      var items = document.querySelectorAll('.slide-nav-item');
      var el = items[idx] ? items[idx].querySelector('.slide-progress-fill') : null;
      if (el) {
        el.style.transition = 'width 0.2s ease-out';
        el.style.width = '0%';
        setTimeout(function () { el.style.transition = 'width 0.1s ease, opacity 0.3s ease'; }, 200);
      }
    }
    function updateCounter(idx) {
      var sn = document.getElementById('slideNumber');
      if (sn) sn.textContent = String(idx + 1).padStart(2, '0');
      var st = document.getElementById('slideTotal');
      if (st) st.textContent = String(slides.length).padStart(2, '0');
    }

    function startAutoSlideTimer() {
      if (!texturesLoaded || !sliderEnabled) return;
      stopAutoSlideTimer();
      var progress = 0;
      var increment = (100 / SLIDE_DURATION()) * PROGRESS_UPDATE_INTERVAL;
      progressAnimation = setInterval(function () {
        if (!sliderEnabled) { stopAutoSlideTimer(); return; }
        progress += increment;
        updateSlideProgress(currentSlideIndex, progress);
        if (progress >= 100) {
          clearInterval(progressAnimation);
          progressAnimation = null;
          fadeSlideProgress(currentSlideIndex);
          if (!isTransitioning) handleSlideChange();
        }
      }, PROGRESS_UPDATE_INTERVAL);
    }
    function stopAutoSlideTimer() {
      if (progressAnimation) clearInterval(progressAnimation);
      if (autoSlideTimer) clearTimeout(autoSlideTimer);
      progressAnimation = null;
      autoSlideTimer = null;
    }
    function safeStartTimer(delay) {
      stopAutoSlideTimer();
      if (sliderEnabled && texturesLoaded) {
        if (delay > 0) autoSlideTimer = setTimeout(startAutoSlideTimer, delay);
        else startAutoSlideTimer();
      }
    }

    function loadImageTexture(src) {
      return new Promise(function (resolve, reject) {
        var l = new THREE.TextureLoader();
        l.load(src, function (t) {
          t.minFilter = t.magFilter = THREE.LinearFilter;
          t.userData = { size: new THREE.Vector2(t.image.width, t.image.height) };
          resolve(t);
        }, undefined, reject);
      });
    }

    function initRenderer() {
      var canvas = document.querySelector('.webgl-canvas');
      if (!canvas) return Promise.resolve();

      scene = new THREE.Scene();
      camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
      renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: false, alpha: false });
      renderer.setSize(window.innerWidth, window.innerHeight);
      // Lower pixel ratio on mobile for performance (max 1.5 vs 2 desktop)
      var maxRatio = window.innerWidth < 768 ? 1.5 : 2;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, maxRatio));

      var s = SLIDER_CONFIG.settings;
      shaderMaterial = new THREE.ShaderMaterial({
        uniforms: {
          uTexture1: { value: null },
          uTexture2: { value: null },
          uProgress: { value: 0 },
          uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
          uTexture1Size: { value: new THREE.Vector2(1, 1) },
          uTexture2Size: { value: new THREE.Vector2(1, 1) },
          uGlobalIntensity: { value: s.globalIntensity },
          uSpeedMultiplier: { value: s.speedMultiplier },
          uDistortionStrength: { value: s.distortionStrength },
          uColorEnhancement: { value: s.colorEnhancement },
          uGlassRefractionStrength: { value: s.glassRefractionStrength },
          uGlassChromaticAberration: { value: s.glassChromaticAberration },
          uGlassBubbleClarity: { value: s.glassBubbleClarity },
          uGlassEdgeGlow: { value: s.glassEdgeGlow },
          uGlassLiquidFlow: { value: s.glassLiquidFlow },
          uImageScale: { value: computeImageScale() },
          uImageOffsetY: { value: computeImageOffsetY() },
          uSquareOffsetY: { value: computeSquareOffsetY() },
          uRectScaleMult: { value: computeRectScaleMult() },
          uRectOffsetY: { value: computeRectOffsetY() }
        },
        vertexShader: vertexShader,
        fragmentShader: fragmentShader
      });
      scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), shaderMaterial));

      var loadPromises = slides.map(function (slide) { return loadImageTexture(slide.media).catch(function(err){ console.warn('texture fail', err); return null; }); });
      return Promise.all(loadPromises).then(function (textures) {
        slideTextures = textures.filter(Boolean);
        if (slideTextures.length >= 2) {
          shaderMaterial.uniforms.uTexture1.value = slideTextures[0];
          shaderMaterial.uniforms.uTexture2.value = slideTextures[1];
          shaderMaterial.uniforms.uTexture1Size.value = slideTextures[0].userData.size;
          shaderMaterial.uniforms.uTexture2Size.value = slideTextures[1].userData.size;
          texturesLoaded = true;
          sliderEnabled = true;
          var wrapper = document.querySelector('.mz-hero');
          if (wrapper) wrapper.classList.add('loaded');
          safeStartTimer(500);
        }
        var render = function () { requestAnimationFrame(render); renderer.render(scene, camera); };
        render();
      });
    }

    // ------ Init ------
    createSlidesNavigation();
    updateCounter(0);

    var tEl = document.getElementById('mainTitle');
    var dEl = document.getElementById('mainDesc');
    var lEl = document.getElementById('mainLabel');
    if (tEl && dEl) {
      tEl.innerHTML = splitText(slides[0].title);
      dEl.textContent = slides[0].description;
      if (lEl) {
        lEl.textContent = slides[0].material || '';
        lEl.style.display = (slides[0].material) ? 'inline-block' : 'none';
      }
      gsap.fromTo(tEl.children, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 1, stagger: 0.03, ease: 'power3.out', delay: 0.5 });
      gsap.fromTo(dEl, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 1, ease: 'power3.out', delay: 0.8 });
      if (lEl) gsap.fromTo(lEl, { y: 10, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9, ease: 'power3.out', delay: 0.35 });
    }

    initRenderer();

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopAutoSlideTimer();
      else if (!isTransitioning) safeStartTimer();
    });
    window.addEventListener('resize', function () {
      if (renderer) {
        renderer.setSize(window.innerWidth, window.innerHeight);
        shaderMaterial.uniforms.uResolution.value.set(window.innerWidth, window.innerHeight);
        shaderMaterial.uniforms.uImageScale.value = computeImageScale();
        shaderMaterial.uniforms.uImageOffsetY.value = computeImageOffsetY();
        shaderMaterial.uniforms.uSquareOffsetY.value = computeSquareOffsetY();
        shaderMaterial.uniforms.uRectScaleMult.value = computeRectScaleMult();
        shaderMaterial.uniforms.uRectOffsetY.value = computeRectOffsetY();
      }
    });

    // ------ Touch swipe (mobile) ------
    var hero = document.getElementById('manomazeHero');
    if (hero) {
      var touchStartX = 0, touchStartY = 0, touchStartTime = 0;
      var SWIPE_MIN = 50;       // min horizontal distance (px)
      var SWIPE_MAX_TIME = 600; // ms
      var SWIPE_MAX_VERTICAL = 80; // ignore vertical swipes (scroll)

      hero.addEventListener('touchstart', function (e) {
        var t = e.changedTouches[0];
        touchStartX = t.clientX;
        touchStartY = t.clientY;
        touchStartTime = Date.now();
      }, { passive: true });

      hero.addEventListener('touchend', function (e) {
        if (isTransitioning || !texturesLoaded || !sliderEnabled) return;
        var t = e.changedTouches[0];
        var dx = t.clientX - touchStartX;
        var dy = t.clientY - touchStartY;
        var dt = Date.now() - touchStartTime;
        if (dt > SWIPE_MAX_TIME) return;
        if (Math.abs(dy) > SWIPE_MAX_VERTICAL) return; // user is scrolling
        if (Math.abs(dx) < SWIPE_MIN) return;

        stopAutoSlideTimer();
        quickResetProgress(currentSlideIndex);
        var nextIdx;
        if (dx < 0) nextIdx = (currentSlideIndex + 1) % slides.length;       // swipe left → next
        else nextIdx = (currentSlideIndex - 1 + slides.length) % slides.length; // swipe right → prev
        navigateToSlide(nextIdx);
      }, { passive: true });
    }

    // ------ Keyboard navigation (desktop) ------
    document.addEventListener('keydown', function (e) {
      if (isTransitioning || !sliderEnabled) return;
      if (e.key === 'ArrowRight') {
        stopAutoSlideTimer();
        quickResetProgress(currentSlideIndex);
        navigateToSlide((currentSlideIndex + 1) % slides.length);
      } else if (e.key === 'ArrowLeft') {
        stopAutoSlideTimer();
        quickResetProgress(currentSlideIndex);
        navigateToSlide((currentSlideIndex - 1 + slides.length) % slides.length);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();

